import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud, X, FileText, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, Badge, Button, toast } from './V2Components';
import { formatBytes } from '../../utils/billFormat';

/** Mirrors the per-file scan caps on the import endpoints (10MB). */
const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Scanner error codes that mean "the service is down", not "this file is bad".
 * If the first few files all fail this way, every remaining file would too —
 * so we abort rather than produce a wall of fix-me cards the user can't act on
 * (and which would then collide with the create endpoints' 20/hr rate limit).
 */
const UPSTREAM_ERROR_CODES = new Set([
  'gemini_error',
  'gemini_not_configured',
  'gemini_truncated_response',
]);
const UPSTREAM_ABORT_THRESHOLD = 2;

let clientIdSeq = 0;
const nextClientId = () => {
  clientIdSeq += 1;
  return `f_${clientIdSeq}`;
};

const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * The shared bulk-import runner: drop N files, walk away, come back to N rows.
 *
 * Clean files are created server-side with no review step — only files with a
 * hard problem surface here. One request per file (ADR-0010), issued
 * sequentially so a mid-import stop leaves a consistent, durable result. Every
 * import endpoint returns the same per-file contract (`outcome`, `fields`,
 * `error`, `error_code`, …), so this component owns the queue, the runner, the
 * progress bar and the summary; callers supply only what differs per document
 * type — the request, the fix-it form, and any notes about the result.
 *
 * @param {{
 *   title: string,
 *   noun: {one: string, many: string},
 *   dropTitle: string,
 *   dropHint: string,
 *   inputTestId: string,
 *   unavailableMessage: string,
 *   importFile: (file: File, opts: {clientId: string}) => Promise<Object>,
 *   recordOf: (result: Object) => ?Object,
 *   onRecordsCreated: (records: Object[]) => void,
 *   renderFixCard: (args: {item: Object, onSaved: Function, onSkip: Function}) => React.ReactNode,
 *   renderNotes?: (createdRecords: Object[]) => React.ReactNode,
 *   onClose: () => void,
 * }} props
 */
const BulkImportPanel = ({
  title,
  noun,
  dropTitle,
  dropHint,
  inputTestId,
  unavailableMessage,
  importFile,
  recordOf,
  onRecordsCreated,
  renderFixCard,
  renderNotes,
  onClose,
}) => {
  const [queue, setQueue] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | running | done
  const [done, setDone] = useState(0);
  const [runTotal, setRunTotal] = useState(0);
  const [results, setResults] = useState([]);
  // clientId -> {status: 'saved' | 'skipped', record?} for needs-attention rows.
  const [resolved, setResolved] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const cancelRef = useRef(false);

  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    const tooBig = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    const usable = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig.length} file${tooBig.length === 1 ? '' : 's'} skipped — over 10MB`);
    }
    setQueue((prev) => [...prev, ...usable.map((file) => ({ clientId: nextClientId(), file }))]);
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer?.files);
  };

  /**
   * Import `entries` one at a time, appending to `previous` results. The first
   * run passes the whole queue; "Import remaining" passes only the entries a
   * stopped or aborted run never reached, keeping what already happened.
   */
  const runImport = async (entries, previous = []) => {
    cancelRef.current = false;
    setPhase('running');
    setRunTotal(entries.length);
    setDone(0);
    setResults(previous);

    const collected = [...previous];
    let processed = 0;
    let upstreamFailures = 0;

    for (const entry of entries) {
      if (cancelRef.current) break;
      let result;
      try {
        result = await importFile(entry.file, { clientId: entry.clientId });
      } catch (err) {
        // Request-level failure (quota, storage down, network). The endpoint
        // returns 200 for every per-file outcome, so reaching here means the
        // rest of the queue would fail the same way.
        toast.error(err.message || 'Import stopped');
        break;
      }

      collected.push({ ...result, clientId: entry.clientId, file: entry.file });
      processed += 1;
      setResults([...collected]);
      setDone(processed);

      const record = result.outcome === 'created' ? recordOf(result) : null;
      if (record) {
        // Merge per file, not at the end: the page fills in live, and a
        // mid-import crash leaves the UI matching the database.
        onRecordsCreated([record]);
      }

      if (UPSTREAM_ERROR_CODES.has(result.error_code)) {
        upstreamFailures += 1;
        if (upstreamFailures >= UPSTREAM_ABORT_THRESHOLD) {
          toast.error(unavailableMessage);
          break;
        }
      } else if (result.outcome === 'created') {
        upstreamFailures = 0;
      }
    }

    setPhase('done');
  };

  // Rows saved from a fix card count as added too: they're real rows now, and
  // the per-document notes should see them.
  const createdRecords = [
    ...results
      .filter((r) => r.outcome === 'created')
      .map(recordOf)
      .filter(Boolean),
    ...Object.values(resolved)
      .filter((entry) => entry.status === 'saved' && entry.record)
      .map((entry) => entry.record),
  ];
  const openAttention = results
    .filter((r) => r.outcome === 'needs_attention' && !resolved[r.clientId])
    .map((r) => ({
      clientId: r.clientId,
      filename: r.original_filename,
      error: r.error,
      fields: r.fields,
      file: r.file,
    }));
  const duplicates = results.filter((r) => r.outcome === 'skipped_duplicate');
  const failures = results.filter((r) => r.outcome === 'failed');
  // Files a stopped or aborted run never reached. Re-importing is idempotent
  // server-side, but the user shouldn't have to remember which files to redo.
  const processedIds = new Set(results.map((r) => r.clientId));
  const remaining = queue.filter((entry) => !processedIds.has(entry.clientId));
  const nounFor = (n) => (n === 1 ? noun.one : noun.many);

  const handleFixSaved = (clientId, record) => {
    setResolved((prev) => ({ ...prev, [clientId]: { status: 'saved', record } }));
    onRecordsCreated([record]);
    toast.success(`${capitalize(noun.one)} added`);
  };
  const handleFixSkipped = (clientId) => setResolved((prev) => ({ ...prev, [clientId]: { status: 'skipped' } }));

  const pct = runTotal ? Math.round((done / runTotal) * 100) : 0;

  return (
    <Card className="p-5 mb-4 border border-[var(--border)]">
      <div className="flex justify-between items-center mb-3 gap-2">
        <h3 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide">{title}</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>

      {phase === 'idle' && (
        <>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            className={`rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
              dragOver ? 'border-[var(--primary)] bg-[var(--bg-hover)]' : 'border-[var(--border)]'
            }`}
          >
            <UploadCloud size={28} className="mx-auto mb-2 text-[var(--text-secondary)]" />
            <p className="text-sm text-[var(--text-main)] mb-1">{dropTitle}</p>
            <p className="text-xs text-[var(--text-secondary)] mb-3">{dropHint}</p>
            <Button type="button" variant="secondary" onClick={() => inputRef.current?.click()}>
              Choose files
            </Button>
            {/* Raw input (the documented useFilePicker exception for visible
                browse flows) with its OWN testid per document type — sharing
                `file-picker-input` with a single-file form would trip
                Playwright strict mode. */}
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              data-testid={inputTestId}
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>

          {queue.length > 0 && (
            <>
              <ul className="mt-4 space-y-1 max-h-60 overflow-y-auto">
                {queue.map((entry) => (
                  <li
                    key={entry.clientId}
                    className="flex items-center gap-2 min-w-0 text-sm text-[var(--text-main)]"
                  >
                    <FileText size={14} className="shrink-0 text-[var(--text-secondary)]" />
                    {/* truncate is load-bearing: without overflow:hidden a long
                        filename paints past the card's rounded edge. */}
                    <span className="truncate min-w-0 flex-1">{entry.file.name}</span>
                    <span className="text-xs text-[var(--text-secondary)] shrink-0">
                      {formatBytes(entry.file.size)}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove ${entry.file.name}`}
                      className="shrink-0 p-1 text-[var(--text-secondary)] hover:text-[var(--error)]"
                      onClick={() => setQueue((prev) => prev.filter((q) => q.clientId !== entry.clientId))}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
                <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={() => setQueue([])}>
                  Clear
                </Button>
                <Button type="button" className="w-full sm:w-auto" onClick={() => runImport(queue)}>
                  {`Import ${queue.length} ${nounFor(queue.length)}`}
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {phase === 'running' && (
        <div>
          <p role="status" aria-live="polite" className="text-sm text-[var(--text-main)] mb-2">
            <Loader2 size={14} className="inline animate-spin mr-2" />
            {`Reading ${done} of ${runTotal}…`}
          </p>
          <div className="w-full h-2 rounded-full bg-[var(--bg-hover)] overflow-hidden">
            <div className="h-full bg-[var(--primary)] transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-end mt-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                cancelRef.current = true;
              }}
            >
              Stop
            </Button>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge status={`${createdRecords.length} added`} />
            {openAttention.length > 0 && <Badge status={`${openAttention.length} need attention`} />}
            {duplicates.length > 0 && <Badge status={`${duplicates.length} already imported`} />}
            {failures.length > 0 && <Badge status={`${failures.length} failed`} />}
            {remaining.length > 0 && <Badge status={`${remaining.length} not imported`} />}
          </div>

          {remaining.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <p className="text-xs text-[var(--text-secondary)]">
                {`The import stopped before ${remaining.length} ${nounFor(remaining.length)}. Nothing was saved for ${remaining.length === 1 ? 'it' : 'them'}.`}
              </p>
              <Button
                type="button"
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => runImport(remaining, results)}
              >
                {`Import remaining ${remaining.length} ${nounFor(remaining.length)}`}
              </Button>
            </div>
          )}

          {renderNotes?.(createdRecords)}

          {failures.length > 0 && (
            <ul className="text-xs text-[var(--text-secondary)] mb-4 space-y-1">
              {failures.map((f) => (
                <li key={f.clientId} className="truncate">
                  {`${f.original_filename}: ${f.error}`}
                </li>
              ))}
            </ul>
          )}

          {openAttention.length > 0 && (
            <>
              <p className="text-sm font-semibold text-[var(--text-main)] mb-2">These need a hand</p>
              {openAttention.map((item) => (
                <React.Fragment key={item.clientId}>
                  {renderFixCard({ item, onSaved: handleFixSaved, onSkip: handleFixSkipped })}
                </React.Fragment>
              ))}
            </>
          )}

          {openAttention.length === 0 && remaining.length === 0 && failures.length === 0 && (
            <p className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
              <CheckCircle2 size={16} className="text-green-500" />
              All done.
            </p>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={() => {
                setQueue([]);
                setResults([]);
                setResolved({});
                setDone(0);
                setRunTotal(0);
                setPhase('idle');
              }}
            >
              Import more
            </Button>
            <Button type="button" className="w-full sm:w-auto" onClick={onClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BulkImportPanel;
